import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import GeneralUtils from '../../shared/utils/GeneralUtils';

dayjs.extend(customParseFormat);

export const UNKNOWN_SUPPLIER_KEY = '__UNKNOWN__';
export const UNKNOWN_SUPPLIER_LABEL = 'Unknown Supplier';

export function getItemSupplierId(item) {
    return item?.supplierId ?? item?.currentSupplierId ?? null;
}

export function getItemSupplierName(item) {
    if (!GeneralUtils.isNullOrEmpty(item?.supplierName)) {
        return item.supplierName;
    }
    if (!GeneralUtils.isNullOrEmpty(item?.currentSupplierName)) {
        return item.currentSupplierName;
    }
    return null;
}

export function findItemIndexInList(itemList, item) {
    const list = itemList || [];
    if (!item) {
        return -1;
    }
    if (item.id != null) {
        const byId = list.findIndex(i => i.id != null && i.id === item.id);
        if (byId >= 0) {
            return byId;
        }
    }
    if (!GeneralUtils.isNullOrEmpty(item.barcode)) {
        const byNewBarcode = list.findIndex(i => !i.id && i.barcode === item.barcode);
        if (byNewBarcode >= 0) {
            return byNewBarcode;
        }
        return list.findIndex(i => i.barcode === item.barcode);
    }
    return -1;
}

export function upsertRequisitionItem(itemList, itemData) {
    const normalizedItem = normalizeItem(itemData);
    const list = [...(itemList || [])];
    let index = findItemIndexInList(list, normalizedItem);
    if (index >= 0) {
        list[index] = {...list[index], ...normalizedItem};
    } else {
        list.push({...normalizedItem, status: normalizedItem.status || 'ACTIVE'});
        index = list.length - 1;
    }
    return {itemList: list, index};
}

export function normalizeItem(item) {
    if (!item) {
        return item;
    }
    const supplierId = getItemSupplierId(item);
    const supplierName = getItemSupplierName(item);
    let expiryDate = item.expiryDate || null;
    if (expiryDate && typeof expiryDate === 'string' && expiryDate.includes('T')) {
        expiryDate = dayjs(expiryDate).format('DD/MM/YYYY');
    } else if (expiryDate && /^\d{4}-\d{2}-\d{2}/.test(String(expiryDate))) {
        expiryDate = dayjs(expiryDate).format('DD/MM/YYYY');
    }
    return {
        ...item,
        supplierId,
        supplierName,
        supplierBssId: item.supplierBssId ?? null,
        currentSupplierId: supplierId,
        currentSupplierName: supplierName,
        mailSent: item.mailSent === true || item.mailSent === 'true',
        mailToEmail: item.mailToEmail || null,
        mailCcEmail: item.mailCcEmail || null,
        damagedQuantity: item.damagedQuantity ?? null,
        deliveredBoxCount: item.deliveredBoxCount ?? null,
        expiryDate
    };
}

export function buildItemPayload(item) {
    const normalized = normalizeItem(item);
    const {
        merchantBranchId,
        currentSupplierId,
        currentSupplierName,
        ...rest
    } = normalized;
    let expiryDate = normalized.expiryDate || null;
    if (expiryDate && typeof expiryDate === 'string' && expiryDate.includes('/')) {
        const parsed = dayjs(expiryDate, 'DD/MM/YYYY');
        expiryDate = parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    }
    return {
        ...rest,
        supplierId: normalized.supplierId,
        supplierName: normalized.supplierName,
        supplierBssId: normalized.supplierBssId,
        damagedQuantity: normalized.damagedQuantity,
        deliveredBoxCount: normalized.deliveredBoxCount,
        expiryDate
    };
}

export function buildRequisitionPayload(requisition) {
    return {
        id: requisition.id,
        merchantBranchId: Number(localStorage.getItem('merchantBranchId')),
        description: requisition.description,
        requisitionStatus: requisition.requisitionStatus || 'OPEN',
        assignedPurchaseRequisitionId: requisition.assignedPurchaseRequisitionId ?? null,
        itemList: (requisition.itemList || []).map(buildItemPayload)
    };
}

export function buildCheckoutPayload(requisition, checkoutOptions) {
    const payload = buildRequisitionPayload(requisition);
    const groups = groupItemsBySupplier(requisition.itemList);
    const checkoutDate = dayjs().format('DD/MM/YYYY');
    const pdfRequisition = {...requisition, checkoutDate};
    payload.checkoutSupplierOptions = groups.map(group => {
        const options = checkoutOptions[group.key] || {};
        const supplierOption = {
            supplierId: group.supplierId,
            supplierName: group.supplierName,
            mail: options.email === true,
            supplierEmail: options.supplierEmail || '',
            ccEmail: options.ccEmail || '',
            description: requisition.description,
            createDate: requisition.createDate,
            checkoutDate,
            requisitionNo: requisition.requisitionNo
        };
        if (supplierOption.mail) {
            supplierOption.attachmentBase64 = buildSupplierPdfBase64(group, pdfRequisition);
            supplierOption.attachmentName = buildSupplierAttachmentName(group);
        }
        return supplierOption;
    });
    return payload;
}

export function normalizeRequisition(requisition) {
    if (!requisition) {
        return requisition;
    }
    const normalized = Object.assign({}, requisition);
    if (normalized.createDate) {
        normalized.createDate = dayjs(normalized.createDate).format('DD/MM/YYYY');
    }
    if (normalized.checkoutDate) {
        normalized.checkoutDate = dayjs(normalized.checkoutDate).format('DD/MM/YYYY');
    }
    if (normalized.approvedDate) {
        normalized.approvedDate = dayjs(normalized.approvedDate).format('DD/MM/YYYY');
    }
    normalized.approved = normalized.approved === true || normalized.approved === 'true';
    normalized.itemList = (normalized.itemList || []).map(normalizeItem);
    return normalized;
}

export function persistRequisitionToDb(requisition, purchaseRequisitionService) {
    const itemList = requisition.itemList || [];
    if (itemList.length === 0) {
        return Promise.resolve({success: false, reason: 'empty'});
    }
    const payload = buildRequisitionPayload(requisition);
    const apiCall = payload.id
        ? purchaseRequisitionService.update(payload)
        : purchaseRequisitionService.create(payload);
    return apiCall.then(response => {
        if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
            return {success: false};
        }
        return {success: true, requisition: normalizeRequisition(response)};
    }).catch(() => ({success: false}));
}

export function applyCostWarningsToItems(itemList, warnings) {
    const warningMap = {};
    (warnings || []).forEach(warning => {
        if (warning?.barcode) {
            warningMap[warning.barcode] = warning;
        }
    });
    return (itemList || []).map(item => {
        const warning = warningMap[item.barcode];
        if (!warning) {
            return normalizeItem(item);
        }
        return normalizeItem({
            ...item,
            cheaperCostAvailable: warning.cheaperCostAvailable === true,
            costAlternatives: warning.alternatives || []
        });
    });
}

export function buildCostWarningsMap(warnings) {
    const warningMap = {};
    (warnings || []).forEach(warning => {
        if (warning?.barcode) {
            warningMap[warning.barcode] = warning;
        }
    });
    return warningMap;
}

export function removeItemFromList(itemList, item) {
    const index = findItemIndexInList(itemList, item);
    if (index < 0) {
        return itemList || [];
    }
    return (itemList || []).filter((_, i) => i !== index);
}

const CHECKOUT_REQUIRED_FIELDS = [
    {key: 'barcode', label: 'Barcode', check: (item) => GeneralUtils.isNullOrEmpty(item.barcode)},
    {key: 'itemName', label: 'Product Name', check: (item) => GeneralUtils.isNullOrEmpty(item.itemName)},
    {
        key: 'supplier',
        label: 'Supplier',
        check: (item) => GeneralUtils.isNullOrEmpty(getItemSupplierId(item))
    },
    {
        key: 'actualOrderAmount',
        label: 'Actual Order Amount',
        check: (item) => isActualOrderAmountMissing(item.actualOrderAmount)
    }
];

function isActualOrderAmountMissing(value) {
    if (value == null || value === '') {
        return true;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return true;
    }
    return false;
}

export function formatCheckoutValidationErrors(errors) {
    if (!errors?.length) {
        return null;
    }
    if (errors.length === 1) {
        return errors[0];
    }
    const preview = errors.slice(0, 2).join(' · ');
    return errors.length > 2
        ? `${preview} (${errors.length - 2} more validation error(s))`
        : preview;
}

export function validateItemsForCheckout(itemList) {
    const errors = [];
    groupItemsBySupplier(itemList).forEach(group => {
        const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
        (group.items || []).forEach(rawItem => {
            const item = normalizeItem(rawItem);
            const barcode = item.barcode || '-';
            CHECKOUT_REQUIRED_FIELDS.forEach(field => {
                if (field.check(item)) {
                    errors.push(
                        `In supplier ${supplierName}, for product with barcode ${barcode}, ${field.label} is not entered`
                    );
                }
            });
        });
    });
    return errors;
}

export function buildInitialCheckoutOptions(itemList) {
    const options = {};
    groupItemsBySupplier(itemList).forEach(group => {
        options[group.key] = {
            supplierName: group.supplierName,
            supplierId: group.supplierId,
            print: false,
            email: false,
            supplierEmail: '',
            ccEmail: getLoggedInUserEmail()
        };
    });
    return options;
}

function buildSupplierPdfDocument(group, requisition) {
    const doc = new jsPDF('portrait', 'pt', 'A4');
    const marginLeft = 40;
    const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
    const requisitionNo = requisition?.requisitionNo || '';
    const orderDate = requisition?.checkoutDate || '';

    doc.setFontSize(14);
    doc.text('Purchase Requisition', marginLeft, 40);
    doc.setFontSize(11);
    let headerY = 58;
    if (requisitionNo) {
        doc.text(`Requisition No: ${requisitionNo}`, marginLeft, headerY);
        headerY += 16;
    }
    doc.text(`Supplier: ${supplierName}`, marginLeft, headerY);
    headerY += 16;
    if (orderDate) {
        doc.text(`Order Date: ${orderDate}`, marginLeft, headerY);
        headerY += 16;
    }

    doc.autoTable({
        startY: headerY + 8,
        head: PDF_TABLE_HEAD,
        body: mapItemsToPdfBody(group.items),
        margin: {left: marginLeft, right: marginLeft}
    });
    return doc;
}

export function buildSupplierAttachmentName(group) {
    const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
    const safeName = supplierName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_') || 'Supplier';
    return `PurchaseRequisition_${safeName}.pdf`;
}

export function buildSupplierPdfBase64(group, requisition) {
    const doc = buildSupplierPdfDocument(group, requisition);
    const dataUri = doc.output('datauristring');
    const commaIndex = dataUri.indexOf(',');
    return commaIndex >= 0 ? dataUri.substring(commaIndex + 1) : dataUri;
}

export function getLoggedInUserEmail() {
    try {
        const hsmUser = JSON.parse(localStorage.getItem('hsmUser'));
        return hsmUser?.email || '';
    } catch (e) {
        return '';
    }
}

export function isValidEmailAddress(email) {
    if (GeneralUtils.isNullOrEmpty(email)) {
        return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

export function validateCheckoutEmailOptions(checkoutOptions) {
    const errors = [];
    Object.keys(checkoutOptions || {}).forEach(groupKey => {
        const options = checkoutOptions[groupKey];
        if (!options?.email) {
            return;
        }
        const supplierName = options.supplierName || UNKNOWN_SUPPLIER_LABEL;
        if (!isValidEmailAddress(options.supplierEmail)) {
            errors.push(`Please enter a valid To email for ${supplierName}`);
        }
        if (!isValidEmailAddress(options.ccEmail)) {
            errors.push(`Please enter a valid CC email for ${supplierName}`);
        }
    });
    return errors;
}

export async function sendRequisitionSupplierEmail(group, requisition, checkoutOption, purchaseRequisitionService) {
    if (!checkoutOption?.email) {
        return {success: true};
    }
    if (!requisition?.id) {
        return {success: false};
    }
    const payload = {
        supplierId: group.supplierId,
        supplierName: group.supplierName,
        supplierEmail: checkoutOption.supplierEmail,
        ccEmail: checkoutOption.ccEmail,
        description: requisition.description,
        createDate: requisition.createDate,
        attachmentBase64: buildSupplierPdfBase64(group, requisition),
        attachmentName: buildSupplierAttachmentName(group)
    };
    const response = await purchaseRequisitionService.sendSupplierEmail(requisition.id, payload);
    if (!response || response.ok === false || !GeneralUtils.isNullOrEmpty(response.type)) {
        return {success: false};
    }
    return {success: true};
}

export function wasSupplierEmailSent(requisition, group) {
    const status = requisition?.requisitionStatus;
    if (status !== 'PURCHASE_ORDER') {
        return false;
    }
    return (group.items || []).some(item => item?.mailSent === true || item?.mailSent === 'true');
}

export function getSupplierMailDetails(group) {
    const item = (group.items || []).find(entry =>
        !GeneralUtils.isNullOrEmpty(entry?.mailToEmail) || !GeneralUtils.isNullOrEmpty(entry?.mailCcEmail)
    );
    if (!item) {
        return null;
    }
    return {
        to: item.mailToEmail || '',
        cc: item.mailCcEmail || ''
    };
}

export function getSupplierGroupKey(item) {
    const supplierId = getItemSupplierId(item);
    if (!GeneralUtils.isNullOrEmpty(supplierId)) {
        return String(supplierId);
    }
    const supplierName = getItemSupplierName(item);
    if (!GeneralUtils.isNullOrEmpty(supplierName)) {
        return supplierName;
    }
    return UNKNOWN_SUPPLIER_KEY;
}

export function getSupplierGroupLabel(item) {
    const supplierName = getItemSupplierName(item);
    return supplierName || UNKNOWN_SUPPLIER_LABEL;
}

export function groupItemsBySupplier(itemList) {
    const groups = new Map();
    (itemList || []).forEach(rawItem => {
        const item = normalizeItem(rawItem);
        const key = getSupplierGroupKey(item);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                supplierId: getItemSupplierId(item),
                supplierName: getSupplierGroupLabel(item),
                supplierBssId: item.supplierBssId,
                items: []
            });
        }
        groups.get(key).items.push(item);
    });
    return Array.from(groups.values()).sort((a, b) =>
        (a.supplierName || '').localeCompare(b.supplierName || '', undefined, {sensitivity: 'base'})
    );
}

export function getItemsInSupplierGroupOrder(itemList) {
    return groupItemsBySupplier(itemList).flatMap(group => group.items);
}

export function getNextItemInSupplierOrder(itemList, currentItem) {
    const orderedItems = getItemsInSupplierGroupOrder(itemList);
    const currentIndex = findItemIndexInList(orderedItems, currentItem);
    if (currentIndex < 0 || currentIndex + 1 >= orderedItems.length) {
        return null;
    }
    return orderedItems[currentIndex + 1];
}

function formatValue(value) {
    if (value == null || value === '') {
        return '';
    }
    return String(value);
}

const PDF_TABLE_HEAD = [[
    'Barcode',
    'Name',
    'Unit',
    'Item Qty',
    'Box Qty',
    'Order Qty'
]];

function mapItemsToPdfBody(items) {
    return (items || []).map(item => [
        formatValue(item.barcode),
        formatValue(item.itemNameWithSizeAndQuantity || item.itemName),
        formatValue(item.itemUnitMeasure),
        formatValue(item.itemSize),
        formatValue(item.boxSize),
        formatValue(item.actualOrderAmount)
    ]);
}

export function exportSupplierPdf(group, requisition) {
    const doc = buildSupplierPdfDocument(group, requisition);
    doc.save(buildSupplierAttachmentName(group));
}

function toQtyNumber(value) {
    if (value == null || value === '') {
        return 0;
    }
    const number = Number(value);
    return Number.isNaN(number) ? 0 : number;
}

/**
 * Compare Purchase Order ordered items vs Delivery Note received items by barcode.
 */
export function buildDeliveryPoComparisonRows(purchaseOrderItems, deliveryNoteItems) {
    const poByBarcode = {};
    (purchaseOrderItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!poByBarcode[barcode]) {
            poByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                orderedQty: 0
            };
        }
        poByBarcode[barcode].orderedQty += toQtyNumber(item.actualOrderAmount);
        if (!poByBarcode[barcode].itemName && item.itemName) {
            poByBarcode[barcode].itemName = item.itemName;
        }
    });

    const dnByBarcode = {};
    (deliveryNoteItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!dnByBarcode[barcode]) {
            dnByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                deliveredQty: 0,
                damagedQty: 0
            };
        }
        const delivered = toQtyNumber(item.actualOrderAmount);
        const damaged = toQtyNumber(item.damagedQuantity);
        dnByBarcode[barcode].deliveredQty += delivered;
        dnByBarcode[barcode].damagedQty += damaged;
        if (!dnByBarcode[barcode].itemName && item.itemName) {
            dnByBarcode[barcode].itemName = item.itemName;
        }
    });

    const barcodes = new Set([...Object.keys(poByBarcode), ...Object.keys(dnByBarcode)]);
    const statusOrder = {UNEXPECTED: 0, MISSING: 1, SHORT: 2, OVER: 3, MATCHED: 4};
    const rows = [];

    barcodes.forEach(barcode => {
        const po = poByBarcode[barcode];
        const dn = dnByBarcode[barcode];
        const orderedQty = po ? po.orderedQty : 0;
        const deliveredQty = dn ? dn.deliveredQty : 0;
        const damagedQty = dn ? dn.damagedQty : 0;
        const netQty = deliveredQty - damagedQty;
        let matchStatus = 'MATCHED';
        let warning = '';

        if (!po && dn) {
            matchStatus = 'UNEXPECTED';
            warning = 'Item was not ordered on Purchase Order';
        } else if (po && !dn) {
            matchStatus = 'MISSING';
            warning = `Ordered ${orderedQty}, nothing received`;
        } else if (deliveredQty < orderedQty) {
            matchStatus = 'SHORT';
            warning = `Ordered ${orderedQty}, received ${deliveredQty}`;
        } else if (deliveredQty > orderedQty) {
            matchStatus = 'OVER';
            warning = `Ordered ${orderedQty}, received ${deliveredQty}`;
        }

        rows.push({
            id: barcode,
            barcode,
            itemName: (dn && dn.itemName) || (po && po.itemName) || '',
            orderedQty,
            deliveredQty,
            damagedQty,
            netQty,
            difference: deliveredQty - orderedQty,
            matchStatus,
            warning
        });
    });

    rows.sort((a, b) => (statusOrder[a.matchStatus] ?? 9) - (statusOrder[b.matchStatus] ?? 9)
        || String(a.barcode).localeCompare(String(b.barcode)));
    return rows;
}

export function countDeliveryPoComparisonIssues(rows) {
    return (rows || []).filter(row => row.matchStatus && row.matchStatus !== 'MATCHED').length;
}

